import type { Scenario } from "../schema/scenario";
import { EXCLUSION_LABEL, STAGE_LABEL, VALUE_TYPE_LABEL } from "../schema/scenario";
import type { CalcResult } from "./types";

const BOM = "﻿";
function cell(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function rows(list: unknown[][]): string {
  return BOM + list.map((r) => r.map(cell).join(",")).join("\n") + "\n";
}

/** 입력값,배출계수,설비별 배출량 , 한 줄이 활동자료 하나 */
export function inputsCsv(scenario: Scenario, result: CalcResult): string {
  const head = ["설비 순서", "단계", "설비명", "설비 위치", "시스템 경계", "제외 사유", "항목", "활동자료 종류", "값", "단위", "값 유형", "값 출처", "환산값", "환산 단위", "배출계수", "계수 단위", "계수 출처", "계수 기준연도", "계수 상태", "배출량(kgCO2eq)", "계산식", "제외 근거", "계수 ID", "계수 GWP 기준", "계수 출처 URL", "전력 믹스 구성 계수"];
  const body = result.byEquipment.flatMap((e) =>
    e.lines.map((l) => [
      e.order + 1, STAGE_LABEL[e.stage], e.name, e.location,
      e.inBoundary ? "포함" : "제외", e.exclusionReason ? EXCLUSION_LABEL[e.exclusionReason] : "",
      l.label, l.kind, l.amount, l.unit, VALUE_TYPE_LABEL[l.valueType as keyof typeof VALUE_TYPE_LABEL] ?? l.valueType, l.source ?? "",
      l.converted, l.convertedUnit ?? "", l.ef?.value ?? "", l.ef?.unit ?? "", l.ef?.source ?? "", l.ef?.baseYear || "해당 없음/미기록", l.ef?.status ?? "",
      l.kgCO2eq, l.formula, scenario.equipment.find((eq) => eq.id === e.equipmentId)?.exclusionNote ?? "", l.ef?.id ?? "", l.ef?.gwpBasis ?? "", l.ef?.sourceUrl ?? "",
      l.ef?.composed ? JSON.stringify(result.electricity.sources.map((s) => ({ sourceId: s.sourceId, share: s.share, factor: s.factor ?? null }))) : "",
    ]),
  );
  return rows([head, ...body]);
}

/** 결과 요약 , 산정기준,기능단위,수소량,총량,단계별 */
export function resultCsv(scenario: Scenario, result: CalcResult): string {
  const list: unknown[][] = [
    ["항목", "값", "단위/비고"],
    ["시나리오", scenario.name, ""],
    ["산정목적", scenario.meta.purpose, ""],
    ["시나리오 기준연도", scenario.meta.baseYear, "배출계수 원본 대표연도와 구분"],
    ["산정방식", result.methodLabel, result.standards.join("; ")],
    ["GWP 기준", result.gwpBasis, ""],
    ["기능단위", `수소 ${result.functionalUnitKg} kg`, `순도 ${scenario.functionalUnit.purityPct}%, ${scenario.functionalUnit.pressureBar} bar, ${scenario.functionalUnit.state === "gas" ? "기체" : "액체"}, ${scenario.functionalUnit.deliveryPoint}`],
    ["산정 기준 수소량", scenario.basis.batchH2Kg, "kg"],
    ["생산량", result.batch.producedKg, "kg"],
    ["출하지점 수소량", result.batch.gateKg, "kg"],
    ["최종 인도 수소량", result.batch.deliveredKg, "kg"],
    ["손실 합계", result.batch.lossKg, "kg"],
    ["분모", result.denominator.kg, result.denominator.kind === "gate" ? "생산시설 출하지점 수소량" : "최종 인도 수소량"],
    ["전력 믹스 배출계수", result.electricity.mixEf, "kgCO2eq/kWh"],
    ...result.electricity.sources.map((s) => [`전력원, ${s.label}`, s.share, `비율 (계수 ${s.ef} kgCO2eq/kWh, ${s.efId})`]),
    ...result.electricity.sources.flatMap((s) => [
      [`전력계수 원본, ${s.label}`, s.factor?.id ?? s.efId, s.factor ? `${s.factor.value} ${s.factor.unit}` : "원본 메타데이터 미기록"],
      [`전력계수 출처, ${s.label}`, s.factor?.source ?? "미기록", s.factor?.sourceUrl ?? "URL 미기록"],
      [`전력계수 대표연도/상태, ${s.label}`, s.factor?.baseYear || "미기록", s.factor?.status ?? "미기록"],
      [`전력계수 GWP, ${s.label}`, s.factor?.gwpBasis ?? "미기록", ""],
    ]),
    ["전력원 입력 합계", result.electricity.enteredKWh ?? "비율 입력", "kWh (전력원 비율 산정용)"],
    ["전력량 측정기간·경계/차이 근거", scenario.electricity.note ?? "미기록", ""],
    ["생산기술과 설비 구성 검토", scenario.basis.technologyReview?.note ?? "해당 없음", ""],
    ["부산물 배분", result.allocation.note, `계수 ${result.allocation.factor}`],
    ["총 온실가스 배출량(배분 전)", result.grossKgCO2eq, "kgCO2eq"],
    ["총 온실가스 배출량", result.totalKgCO2eq, "kgCO2eq"],
    ["1 kg당 배출량", result.perKgH2 ?? "계산 제한", "kgCO2eq/kgH2"],
    ["기능단위당 배출량", result.perFunctionalUnitKgCO2eq ?? "계산 제한/이전 기록", `kgCO2eq/FU (수소 ${result.functionalUnitKg} kg)`],
    ...result.byStage.map((s) => [`단계별, ${STAGE_LABEL[s.stage]}`, s.kgCO2eq, `kgCO2eq (${(s.share * 100).toFixed(1)}%)`]),
    ...result.byEquipment.map((e) => [`설비별, ${e.name}`, e.kgCO2eq, `kgCO2eq (${(e.share * 100).toFixed(1)}%)${e.inBoundary ? "" : ", 제외"}`]),
    ...result.byEquipment.filter((e) => !e.inBoundary).map((e) => [`제외 근거, ${e.name}`, e.exclusionReason ? EXCLUSION_LABEL[e.exclusionReason] : "미기록", scenario.equipment.find((eq) => eq.id === e.equipmentId)?.exclusionNote ?? (e.forcedOut ? "산정방식의 경계 규칙 적용" : "미기록")]),
  ];
  if (result.cert) {
    list.push(["인증제 적용조건", result.cert.eligible ? "충족" : "미충족", result.cert.reasons.join("; ")]);
    list.push(["인증제 등급(교육용 참고)", result.cert.grade ?? "해당 없음", result.cert.thresholdKgCO2eq !== null ? `청정수소 기준 ${result.cert.thresholdKgCO2eq} kgCO2eq/kgH2 이하` : ""]);
  }
  list.push(["안내", "교육용 모의산정 결과로서 종합적인 환경영향평가, 제3자 검증 또는 청정수소 인증기관의 공식 판정을 대체하지 않습니다", ""]);
  for (const i of result.issues) list.push([`검증, ${i.level}`, i.message, i.code]);
  return rows(list);
}
